/**
 * MerchantBannerResults — merchant-grade results for self-serve banner tests,
 * built to the 0.2 handoff (docs/design/design_handoff_merchant 0.2/resultados).
 *
 * Six states, plain Spanish, zero stats vocabulary. Rates are ALWAYS phrased as
 * "N de cada 100 hicieron clic" — never a percentage, never CTR/lift. Green is
 * reserved for the one success state (the new banner won); every other outcome
 * is framed as useful information, not a loss.
 *
 * This is also the only screen that can stop a running test.
 */

import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Card, Text, Tag, Button, ProgressBar } from '@nimbus-ds/components'
import { experimentService } from '@/api/experiments'
import { repeatExperiment } from '@/lib/repeatExperiment'
import { nextVersionName } from '@/components/experiments/RepeatExperimentModal'
import { trackAppEvent } from '@/lib/appTelemetry'
import { useSetMerchantChrome } from '@/components/layout/MerchantChrome'
import { fmtInt } from '@/lib/formatEsAr'
import type { Experiment } from '@/types'

/** The run's cap: the test stops here even if nothing separated (→ draw). */
const CAP_DAYS = 30

/**
 * A self-serve banner A/B — explicitly NOT an optimization slot. A slot carries the same marker but
 * on variant_b/variant_c arms and answers a different question, so it has its own surface
 * (MerchantBannerOptimization). The mode check is belt-and-braces on top of the `treatment` key.
 */
export function isSelfServeBannerExperiment(exp: Experiment): boolean {
  if (exp.mode === 'optimization') return false
  return !!(exp.variants as any)?.treatment?.config?.self_serve_banner
}

const dayMs = 86_400_000
const startOf = (exp: Experiment) => new Date(exp.created_at || Date.now()).getTime()
const endOf = (exp: Experiment) => {
  const raw = exp.timing?.end_date_time ? new Date(exp.timing.end_date_time).getTime() : NaN
  return Number.isFinite(raw) ? raw : startOf(exp) + CAP_DAYS * dayMs
}

/** Whole days the test has been running (capped at the 30-day ceiling). */
export function daysElapsed(exp: Experiment): number {
  const end = Math.min(Date.now(), endOf(exp))
  return Math.max(1, Math.min(CAP_DAYS, Math.ceil((end - startOf(exp)) / dayMs)))
}

/**
 * Progress toward the CAP, not toward a session target: the verdict lands
 * whenever the risk-posture criteria are satisfied, and the cap is what
 * guarantees the test ends — as a draw if nothing separated.
 */
export function timeProgressPct(exp: Experiment): number {
  const start = startOf(exp)
  const end = endOf(exp)
  if (end <= start) return 0
  return Math.min(95, Math.max(2, Math.round(((Date.now() - start) / (end - start)) * 100)))
}

function reachedCap(exp: Experiment): boolean {
  return exp.status === 'completed' || Date.now() >= endOf(exp)
}

export type ResultKind =
  | 'collecting'
  | 'winner_new'
  | 'winner_current'
  | 'no_difference'
  | 'vetoed'
  | 'stopped_admin'
  | 'stopped_merchant'

/**
 * The decision, read post-ladder when the payload carries it: `decision_eta` +
 * top-level `winner` fold in the purchase guardrail (a click-winner that hurts
 * sales is vetoed; confident sales can decide when clicks can't). A payload from
 * before the ladder falls back to the proxy's own bb_verdict, which is
 * hard-gated to inconclusive at small N — no second bar here.
 */
function decidedKind(results: any | null): ResultKind | null {
  if (!results) return null
  const eta = results.decision_eta
  if (eta) {
    if (eta.state === 'vetoed') return 'vetoed'
    if (eta.state === 'no_difference') return 'no_difference'
    if (eta.state === 'winner') {
      if (results.winner === 'treatment') return 'winner_new'
      if (results.winner === 'control') return 'winner_current'
    }
    return null
  }
  const verdict = results.bb_verdict
  if (verdict === 'treatment_better') return 'winner_new'
  if (verdict === 'control_better') return 'winner_current'
  return null
}

function deriveKind(exp: Experiment, results: any | null): ResultKind {
  const decided = decidedKind(results)
  if (exp.status === 'paused') {
    // Drift = we stopped it because the storefront banner changed underneath;
    // that invalidates the comparison even if a verdict had already landed.
    if (exp.auto_stopped_reason === 'control_drift') return 'stopped_admin'
    // Stopping a test that already had its answer doesn't erase the answer.
    return decided ?? 'stopped_merchant'
  }
  if (decided) return decided
  if (reachedCap(exp)) return 'no_difference'
  return 'collecting'
}

/** Clicks per 100 visits: whole at ≥10, one decimal below. Never a percentage. */
const per100 = (rate: number) => {
  const v = (rate || 0) * 100
  return v >= 10 ? String(Math.round(v)) : (Math.round(v * 10) / 10).toFixed(1)
}

const fmtDate = (ms: number) =>
  new Date(ms).toLocaleDateString('es-AR', { day: 'numeric', month: 'long' })

/** First promoted challenger image in the variant config (swap or insert). */
function treatmentImage(exp: Experiment): string | null {
  const cfg = (exp.variants as any)?.treatment?.config || {}
  for (const s of cfg.elements_to_swap || []) {
    if (s.image_url_desktop) return s.image_url_desktop
    if (s.image_url_mobile) return s.image_url_mobile
  }
  for (const i of cfg.elements_to_insert || []) {
    if (i.image_url) return i.image_url
  }
  return null
}

function controlImage(exp: Experiment): string | null {
  const marker = (exp.variants as any)?.treatment?.config?.self_serve_banner
  return marker?.devices?.desktop?.control_image || marker?.control_image || null
}

/**
 * The banner's real proportions, recorded when the merchant confirmed it. Used
 * so the thumbnails keep their own shape — a store whose banner isn't the usual
 * wide strip shouldn't see it squashed into one.
 */
function controlAspect(exp: Experiment): string {
  const marker = (exp.variants as any)?.treatment?.config?.self_serve_banner
  const size = marker?.devices?.desktop?.control_size || marker?.control_size
  return size?.width && size?.height ? `${size.width} / ${size.height}` : '16 / 5'
}

interface ResultView {
  kind: ResultKind
  controlRate: number
  treatmentRate: number
  participants: number
  days: number
  progressPct: number
  upliftPct: number | null
  eta: any | null
  /** The purchase guardrail made the call, not clicks — the hero says "ventas". */
  decidedOnSales: boolean
}

function buildView(exp: Experiment, results: any | null): ResultView {
  const sessions = results?.sessions || {}
  const rates = results?.conversion_rate || {}
  const c = rates.control || 0
  const t = rates.treatment || 0
  return {
    kind: deriveKind(exp, results),
    controlRate: c,
    treatmentRate: t,
    participants: (sessions.control || 0) + (sessions.treatment || 0),
    days: daysElapsed(exp),
    progressPct: timeProgressPct(exp),
    upliftPct: c > 0 ? Math.round(((t - c) / c) * 100) : null,
    eta: results?.decision_eta || null,
    decidedOnSales: results?.decision_eta?.decided_on === 'guardrail',
  }
}

/**
 * ETA floor: below this many participating sessions the projection is too noisy
 * to put in front of a merchant — early lifts regress toward their true value,
 * so a day-1 estimate would only grow and read like a broken promise.
 */
const ETA_MIN_SESSIONS = 200

/** The collecting card's bottom line: a concrete ETA when we can stand behind one. */
function etaLine(v: ResultView): string {
  const capLine = `Si a los ${CAP_DAYS} días no hay una diferencia clara, cerramos la prueba y te lo decimos.`
  const eta = v.eta
  if (!eta || eta.sessions_so_far < ETA_MIN_SESSIONS) return capLine
  if (eta.state === 'wait' && eta.days_estimated != null) {
    const d = Math.ceil(eta.days_estimated)
    return eta.days_estimated < 1
      ? 'Respuesta estimada en menos de un día, si el ritmo de visitas se mantiene.'
      : `Respuesta estimada en ~${d} día${d === 1 ? '' : 's'}, si el ritmo de visitas se mantiene.`
  }
  if (eta.state === 'around_margin') {
    return `Vienen muy parejos: puede que termine sin un ganador claro. Si a los ${CAP_DAYS} días sigue así, cerramos la prueba y te lo decimos.`
  }
  return capLine
}

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------

const HERO: Record<ResultKind, { emoji: string; title: string; body: (v: ResultView) => string }> = {
  collecting: {
    emoji: '🔎',
    title: 'Estamos midiendo',
    body: () => 'Todavía no hay una respuesta confiable. Te avisamos apenas la haya.',
  },
  winner_new: {
    emoji: '🏆',
    title: 'Ganó tu banner nuevo',
    body: (v) =>
      v.decidedOnSales
        ? 'Con el banner nuevo tu tienda vendió más. Ya podés dejarlo fijo.'
        : v.upliftPct && v.upliftPct > 0
          ? `Genera un ${v.upliftPct}% más de clics que el que tenías. Ya podés dejarlo fijo.`
          : 'Genera más clics que el que tenías. Ya podés dejarlo fijo.',
  },
  winner_current: {
    emoji: '✅',
    title: 'Tu banner de siempre sigue siendo el mejor',
    body: (v) =>
      v.decidedOnSales
        ? 'Tus ventas fueron mejores con tu banner de siempre. No hace falta que cambies nada.'
        : 'No hace falta que cambies nada. Ahora lo sabés con datos, no por intuición.',
  },
  no_difference: {
    emoji: '🤝',
    title: 'Sin diferencia clara',
    body: () =>
      'Los dos banners funcionaron parecido. Quedate con el que más te guste — esta vez la imagen no es lo que mueve la aguja.',
  },
  vetoed: {
    emoji: '⚖️',
    title: 'Más clics, pero menos ventas',
    body: () =>
      'El banner nuevo consigue más clics, pero tus ventas fueron mejores con el de siempre. Te recomendamos quedarte con tu banner actual.',
  },
  stopped_admin: {
    emoji: '⏸️',
    title: 'Frenamos la prueba',
    body: () =>
      'Cambiaste el banner de tu tienda desde el administrador, así que lo que medíamos dejó de ser comparable.',
  },
  stopped_merchant: {
    emoji: '⏸️',
    title: 'Frenaste la prueba',
    body: () =>
      'Tu tienda volvió a mostrar solo tu banner de siempre. Lo medido hasta acá quedó guardado.',
  },
}

/** Only the winner state earns green; the rest stay informational. */
const HERO_SURFACE: Record<ResultKind, string> = {
  collecting: 'bg-transparent',
  winner_new: 'rounded-lg border border-[color-mix(in_srgb,#00935b_25%,#eaf7f0)] bg-[#eaf7f0] p-4',
  winner_current: 'rounded-lg border border-[color-mix(in_srgb,#0059d5_25%,#eef5ff)] bg-[#eef5ff] p-4',
  no_difference: 'rounded-lg border border-[#e7e7e7] bg-white p-4',
  vetoed: 'rounded-lg border border-[#e7e7e7] bg-white p-4',
  stopped_admin: 'rounded-lg border border-[#e7e7e7] bg-white p-4',
  stopped_merchant: 'rounded-lg border border-[#e7e7e7] bg-white p-4',
}

function Hero({ v }: { v: ResultView }) {
  const h = HERO[v.kind]
  return (
    <div className={`flex items-start gap-3 ${HERO_SURFACE[v.kind]}`}>
      <span className="text-[28px] leading-none" aria-hidden="true">{h.emoji}</span>
      <div className="flex min-w-0 flex-col gap-1">
        <span className="text-lg font-bold leading-tight text-[var(--nimbus-colors-neutral-textHigh)]">
          {h.title}
        </span>
        <Text>{h.body(v)}</Text>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Comparison
// ---------------------------------------------------------------------------

function BannerRow({
  label,
  src,
  rate,
  aspect,
  tag,
  winner,
  muted,
}: {
  label: string
  src: string | null
  rate: number
  aspect: string
  tag?: { appearance: 'success' | 'primary'; text: string }
  winner?: boolean
  muted?: boolean
}) {
  return (
    <div className="flex items-center gap-3">
      {src ? (
        <img
          src={src}
          alt={label}
          className="w-24 shrink-0 rounded-md border border-[#e7e7e7] object-cover"
          style={{ aspectRatio: aspect }}
        />
      ) : (
        <div
          className="w-24 shrink-0 rounded-md bg-gradient-to-br from-[#d8d8d8] to-[#c2c2c2]"
          style={{ aspectRatio: aspect }}
        />
      )}
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <Text fontSize="caption">{label}</Text>
          {tag && (
            <div className="shrink-0 whitespace-nowrap">
              <Tag appearance={tag.appearance}>{tag.text}</Tag>
            </div>
          )}
        </div>
        <span className={`text-sm ${muted ? 'text-[#5d5d5d]' : 'text-[var(--nimbus-colors-neutral-textHigh)]'}`}>
          <strong className={`tabular-nums ${winner ? 'text-[#00935b]' : ''}`}>{per100(rate)}</strong>{' '}
          de cada 100 hicieron clic
        </span>
      </div>
    </div>
  )
}

function Comparison({ v, exp }: { v: ResultView; exp: Experiment }) {
  const stopped = v.kind === 'stopped_admin' || v.kind === 'stopped_merchant'
  // A stopped test collapses to one honest line — the arms never got a fair run.
  if (stopped) {
    return (
      <Text fontSize="caption">
        {v.kind === 'stopped_admin'
          ? `Alcanzó a medir ${v.days} días y ${fmtInt(v.participants)} visitas: ${per100(v.controlRate)} contra ${per100(v.treatmentRate)} de cada 100. No es suficiente para sacar una conclusión.`
          : `${v.days} días y ${fmtInt(v.participants)} visitas: ${per100(v.controlRate)} contra ${per100(v.treatmentRate)} de cada 100.`}
      </Text>
    )
  }

  const footer =
    v.kind === 'collecting'
      ? 'Con estos números todavía puede cambiar. Esperá a que te avisemos.'
      : `${fmtInt(v.participants)} visitas participaron en ${v.days} días.`

  return (
    <section className="flex flex-col gap-2">
      <Text fontWeight="medium">{v.kind === 'collecting' ? 'Cómo van' : 'Cómo terminaron'}</Text>
      <Card>
        <Card.Body>
          <div className="flex flex-col gap-4">
            <BannerRow
              label="Tu banner de hoy"
              src={controlImage(exp)}
              rate={v.controlRate}
              aspect={controlAspect(exp)}
              tag={
                v.kind === 'winner_current'
                  ? { appearance: 'primary', text: 'El mejor' }
                  : v.kind === 'vetoed'
                    ? { appearance: 'primary', text: 'Recomendado' }
                    : undefined
              }
            />
            <BannerRow
              label="El nuevo"
              src={treatmentImage(exp)}
              rate={v.treatmentRate}
              aspect={controlAspect(exp)}
              tag={v.kind === 'winner_new' ? { appearance: 'success', text: 'Ganador' } : undefined}
              winner={v.kind === 'winner_new'}
              muted={v.kind === 'winner_current' || v.kind === 'vetoed'}
            />
            <div className="border-t border-[#f0f0f0] pt-3">
              <Text fontSize="caption">{footer}</Text>
            </div>
          </div>
        </Card.Body>
      </Card>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function MerchantBannerResults() {
  const navigate = useNavigate()
  const [exp, setExp] = useState<Experiment | null>(null)
  const [view, setView] = useState<ResultView | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [confirmStop, setConfirmStop] = useState(false)
  const [stopping, setStopping] = useState(false)
  const [relaunching, setRelaunching] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  useSetMerchantChrome({
    title: 'Tu prueba de banner',
    subtitle: exp
      ? view && view.kind !== 'collecting'
        ? `Terminó el ${fmtDate(Math.min(Date.now(), endOf(exp)))} · ${view.days} días`
        : `Empezó el ${fmtDate(startOf(exp))}`
      : undefined,
    backTo: '/merchant',
    showStoreOnDesktop: true,
  })

  const load = async () => {
    try {
      const all = await experimentService.getExperiments()
      const mine = all
        .filter(isSelfServeBannerExperiment)
        .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
      const latest = mine[0] || null
      if (!latest) {
        setExp(null)
        return
      }
      const results = await experimentService.getExperimentResults(latest.id).catch(() => null)
      setExp(latest)
      setView(buildView(latest, results))
    } catch {
      setError('No pudimos cargar tus pruebas. Probá de nuevo en un rato.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    trackAppEvent('results_viewed')
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const stopTest = async () => {
    if (!exp || stopping) return
    setStopping(true)
    try {
      // Pausing regenerates the storefront bundle, so the store goes back to
      // showing only the original banner. Measurements already taken persist.
      await experimentService.pauseExperiment(exp.id)
      trackAppEvent('test_stopped', { experiment_id: exp.id })
      setConfirmStop(false)
      await load()
    } catch {
      setError('No pudimos frenar la prueba. Probá de nuevo en un rato.')
    } finally {
      setStopping(false)
    }
  }

  /**
   * Re-run the SAME matchup (same challenger, same control) as a fresh test —
   * the consultant's "Repetir": backend clones with clean counters and archives
   * this run. For a new challenger the wizard is the path, not this.
   */
  const relaunch = async () => {
    if (!exp || relaunching) return
    setRelaunching(true)
    setActionError(null)
    try {
      const clone = await repeatExperiment(exp, {
        name: nextVersionName(exp.name),
        note: '',
        timing: 'same_duration',
        launch: true,
      })
      trackAppEvent('test_relaunched', { experiment_id: clone.id, source_id: exp.id })
      setLoading(true)
      await load()
    } catch {
      setActionError('No pudimos volver a lanzar la prueba. Probá de nuevo en un rato.')
    } finally {
      setRelaunching(false)
    }
  }

  if (loading) return <Text className="py-6 text-center">Cargando…</Text>
  if (error) {
    return (
      <Card><Card.Body><Text className="text-center">{error}</Text></Card.Body></Card>
    )
  }
  if (!exp || !view) {
    return (
      <Card>
        <Card.Body>
          <Text className="text-center">
            Todavía no iniciaste ninguna prueba de banner.{' '}
            <Link to="/merchant/banner-test" className="underline">Empezá acá.</Link>
          </Text>
        </Card.Body>
      </Card>
    )
  }

  const winnerImage = treatmentImage(exp)

  return (
    <div className="flex flex-col gap-5 md:gap-6">
      <Hero v={view} />

      <div className="flex flex-col gap-5 md:grid md:grid-cols-[1fr_380px] md:items-start md:gap-6">
        <Comparison v={view} exp={exp} />

        <div className="flex flex-col gap-4">
          {view.kind === 'collecting' && (
            <Card>
              <Card.Body>
                <div className="flex flex-col gap-2">
                  <div className="flex items-baseline justify-between gap-3">
                    <Text fontWeight="medium" color="neutral-textHigh">
                      Día {view.days} de {CAP_DAYS}
                    </Text>
                    <Text fontSize="caption">
                      {fmtInt(view.participants)} visitas participaron
                    </Text>
                  </div>
                  <ProgressBar value={view.progressPct} appearance="primary" />
                  <Text fontSize="caption">{etaLine(view)}</Text>
                  <Text fontSize="caption">
                    También miramos tus ventas: un banner que junta clics pero vende menos no va a
                    ganar.
                  </Text>
                </div>
              </Card.Body>
            </Card>
          )}

          {view.kind === 'winner_new' && (
            <Card>
              <Card.Body>
                <div className="flex flex-col gap-3">
                  <Text fontWeight="medium" color="neutral-textHigh">Dejalo fijo en tu tienda</Text>
                  <Text>
                    {exp.status === 'active'
                      ? 'Ya hay un ganador, así que no hace falta seguir midiendo. Terminá la prueba y subí el banner nuevo desde el administrador de tu tienda para dejarlo fijo.'
                      : 'La prueba ya terminó, así que tu tienda volvió a mostrar el banner de siempre. Para que el nuevo quede puesto, subilo desde el administrador de tu tienda.'}
                  </Text>
                  {exp.status === 'active' && (
                    <Button appearance="primary" onClick={stopTest} disabled={stopping}>
                      {stopping ? 'Terminando…' : 'Terminar la prueba'}
                    </Button>
                  )}
                  <a
                    href="https://www.tiendanube.com/admin"
                    target="_blank"
                    rel="noreferrer"
                    className="no-underline"
                  >
                    <Button appearance={exp.status === 'active' ? 'neutral' : 'primary'}>
                      Ir al administrador
                    </Button>
                  </a>
                  {winnerImage && (
                    <a href={winnerImage} download target="_blank" rel="noreferrer" className="no-underline">
                      <Button appearance="transparent">Descargar el banner ganador</Button>
                    </a>
                  )}
                </div>
              </Card.Body>
            </Card>
          )}

          {(view.kind === 'winner_current' || view.kind === 'no_difference' || view.kind === 'vetoed') && (
            <div className="flex flex-col gap-2">
              <Button appearance="primary" onClick={() => navigate('/merchant/banner-test')}>
                Probar otro banner
              </Button>
              {exp.status === 'active' && (
                <Button appearance="transparent" onClick={stopTest} disabled={stopping}>
                  {stopping ? 'Terminando…' : 'Terminar la prueba'}
                </Button>
              )}
              {view.kind === 'no_difference' && (
                <Text fontSize="caption">
                  Si te gusta más el nuevo, podés dejarlo desde el administrador de tu tienda.
                </Text>
              )}
            </div>
          )}

          {view.kind === 'stopped_merchant' && (
            <div className="flex flex-col gap-2">
              <Button appearance="primary" onClick={relaunch} disabled={relaunching}>
                {relaunching ? 'Lanzando…' : 'Volver a lanzar esta prueba'}
              </Button>
              <Button appearance="transparent" onClick={() => navigate('/merchant/banner-test')}>
                Probar otro banner
              </Button>
              <Text fontSize="caption">
                Volver a lanzarla arranca la misma comparación de cero, con el contador en cero.
              </Text>
              {actionError && <Text fontSize="caption">{actionError}</Text>}
            </div>
          )}

          {view.kind === 'stopped_admin' && (
            <Button appearance="primary" onClick={() => navigate('/merchant/banner-test')}>
              Empezar una prueba nueva
            </Button>
          )}

          {/* Stopping lives here and nowhere else — "siempre frenable". */}
          {view.kind === 'collecting' && !confirmStop && (
            <Button appearance="transparent" onClick={() => setConfirmStop(true)}>
              Frenar la prueba
            </Button>
          )}
          {view.kind === 'collecting' && confirmStop && (
            <Card>
              <Card.Body>
                <div className="flex flex-col gap-2">
                  <Text fontWeight="medium" color="neutral-textHigh">¿Frenar la prueba?</Text>
                  <Text fontSize="caption">
                    Tu tienda vuelve a mostrar solo tu banner de hoy. Lo medido hasta acá queda
                    guardado, pero todavía no alcanza para saber cuál gana.
                  </Text>
                  <div className="flex items-center justify-end gap-2">
                    <Button appearance="transparent" onClick={() => setConfirmStop(false)}>
                      Seguir midiendo
                    </Button>
                    <Button appearance="danger" onClick={stopTest} disabled={stopping}>
                      {stopping ? 'Frenando…' : 'Frenar'}
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
